import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Inbox, Users, TrendingUp, CheckCircle2, Mail, KeyRound, Copy, Download, ShieldCheck, CreditCard, Euro, FileText, Undo2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = { nuevo: "Nuevo", contactado: "Contactado", en_curso: "En curso", cerrado: "Cerrado" };
const STATUS_COLORS = {
  nuevo: "bg-[#e879f9]/15 text-[#e879f9] border-[#e879f9]/40",
  contactado: "bg-[#38bdf8]/15 text-[#38bdf8] border-[#38bdf8]/40",
  en_curso: "bg-amber-400/15 text-amber-300 border-amber-400/40",
  cerrado: "bg-emerald-400/15 text-emerald-300 border-emerald-400/40",
};

export default function AdminDashboard() {
  const { user, logout, authHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("todos");
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [encKey, setEncKey] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [chargeFor, setChargeFor] = useState(null);
  const [chargeAmount, setChargeAmount] = useState("");
  const [chargeConcept, setChargeConcept] = useState("");
  const [charging, setCharging] = useState(false);
  const [lastCheckout, setLastCheckout] = useState(null);
  const [payments, setPayments] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = { withCredentials: true, headers: authHeaders() };
      const [s, l, k, p] = await Promise.all([
        axios.get(`${API}/admin/stats`, cfg),
        axios.get(`${API}/admin/leads`, cfg),
        axios.get(`${API}/admin/encryption-key`, cfg),
        axios.get(`${API}/admin/payments`, cfg),
      ]);
      setStats(s.data);
      setLeads(l.data);
      setEncKey(k.data);
      setPayments(p.data);
    } catch {
      toast.error("No se pudieron cargar los datos del dashboard");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { status }, { withCredentials: true, headers: authHeaders() });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Estado actualizado: ${STATUS_LABELS[status]}`);
    } catch {
      toast.error("No se pudo actualizar el estado");
    }
  };

  const removeLead = async (id) => {
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { withCredentials: true, headers: authHeaders() });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Solicitud eliminada");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const sendReply = async (id) => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      await axios.post(`${API}/admin/leads/${id}/reply`, { message: replyText }, { withCredentials: true, headers: authHeaders() });
      toast.success("Respuesta enviada por email");
      setReplyFor(null);
      setReplyText("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar la respuesta");
    } finally {
      setSendingReply(false);
    }
  };

  const rotateKey = async () => {
    setRotating(true);
    try {
      const { data } = await axios.post(`${API}/admin/encryption-key/rotate`, {}, { withCredentials: true, headers: authHeaders() });
      setEncKey(data);
      toast.success("Clave de cifrado regenerada");
    } catch {
      toast.error("No se pudo regenerar la clave");
    } finally {
      setRotating(false);
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(encKey.public_key);
    toast.success("Clave pública copiada");
  };

  const downloadKey = () => {
    const blob = new Blob([encKey.public_key], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "eladpentester_ceo_public_key.asc";
    a.click();
  };

  const createCharge = async (id) => {
    const amount = parseFloat(chargeAmount);
    if (!amount || amount <= 0 || !chargeConcept.trim()) {
      toast.error("Indica un importe y un concepto válidos");
      return;
    }
    setCharging(true);
    try {
      const { data } = await axios.post(`${API}/admin/leads/${id}/charge`,
        { amount, concept: chargeConcept, send_email: true },
        { withCredentials: true, headers: authHeaders() });
      setLastCheckout({ leadId: id, url: data.checkout_url, emailed: data.emailed });
      toast.success(data.emailed ? "Enlace de pago creado y enviado por email" : "Enlace de pago creado (email no enviado: saldo de email agotado)");
      setChargeFor(null);
      setChargeAmount("");
      setChargeConcept("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo crear el cobro");
    } finally {
      setCharging(false);
    }
  };

  const copyCheckout = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Enlace de pago copiado");
  };

  const downloadInvoice = async (sessionId, number) => {
    try {
      const resp = await axios.get(`${API}/admin/invoices/${sessionId}/download`, {
        withCredentials: true, headers: authHeaders(), responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${number || "factura"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Factura descargada");
    } catch {
      toast.error("No se pudo descargar la factura");
    }
  };

  const refundPayment = async (p) => {
    const input = window.prompt(`Reembolso de "${p.concept}" (${p.amount?.toFixed(2)} €).\nImporte en EUR a devolver — vacío = reembolso total:`);
    if (input === null) return;
    const payload = input.trim() === "" ? {} : { amount: parseFloat(input.replace(",", ".")) };
    if (payload.amount !== undefined && (isNaN(payload.amount) || payload.amount <= 0)) {
      toast.error("Importe no válido");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/admin/payments/${p.session_id}/refund`, payload,
        { withCredentials: true, headers: authHeaders() });
      toast.success(`Reembolsados ${data.refunded_amount.toFixed(2)} €`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo reembolsar");
    }
  };

  const filtered = filter === "todos" ? leads : leads.filter((l) => l.status === filter);

  return (
    <div className="min-h-screen bg-[#0d0914] text-[#f5f3ff] f-text" data-testid="admin-dashboard">
      {/* Topbar */}
      <nav className="border-b border-[#2e1065] bg-[#0d0914]/90 backdrop-blur sticky top-0 z-40 px-6 py-4" data-testid="admin-nav">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.jpg" alt="ELADPENTESTER" className="w-9 h-9 rounded-lg object-cover border border-[#581c87]" />
            <div>
              <span className="f-poster text-lg text-white uppercase tracking-wide block leading-none">CEO Command Center</span>
              <span className="text-[9px] f-mono text-[#d8b4fe] tracking-widest uppercase" data-testid="admin-user-email">{user?.email}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="f-mono text-[10px] uppercase tracking-widest text-[#d8b4fe] hover:text-[#e879f9] transition-colors flex items-center gap-1" data-testid="refresh-btn">
              <RefreshCw className="w-3.5 h-3.5" /> Actualizar
            </button>
            <Link to="/" className="f-mono text-[10px] uppercase tracking-widest text-[#d8b4fe] hover:text-[#e879f9] transition-colors" data-testid="view-site-link">Ver web</Link>
            <button onClick={logout} className="f-poster uppercase text-xs tracking-wide border border-[#581c87] text-white px-4 py-2 rounded hover:border-[#e879f9] transition-colors flex items-center gap-2" data-testid="logout-btn">
              <LogOut className="w-3.5 h-3.5" /> Salir
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        {/* Stats */}
        <section data-testid="stats-section">
          <h2 className="f-poster text-2xl uppercase text-white mb-6">Resumen General</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="border border-[#3b126d] bg-[#140b22] rounded-xl p-5" data-testid="stat-total">
              <Inbox className="w-5 h-5 text-[#e879f9] mb-2" />
              <div className="f-poster text-3xl text-white">{stats?.total ?? "—"}</div>
              <div className="f-mono text-[10px] uppercase tracking-widest text-[#d8b4fe]">Solicitudes totales</div>
            </div>
            <div className="border border-[#3b126d] bg-[#140b22] rounded-xl p-5" data-testid="stat-nuevo">
              <TrendingUp className="w-5 h-5 text-[#e879f9] mb-2" />
              <div className="f-poster text-3xl text-[#e879f9]">{stats?.by_status?.nuevo ?? "—"}</div>
              <div className="f-mono text-[10px] uppercase tracking-widest text-[#d8b4fe]">Nuevas</div>
            </div>
            <div className="border border-[#3b126d] bg-[#140b22] rounded-xl p-5" data-testid="stat-en-curso">
              <Users className="w-5 h-5 text-amber-300 mb-2" />
              <div className="f-poster text-3xl text-amber-300">{stats?.by_status?.en_curso ?? "—"}</div>
              <div className="f-mono text-[10px] uppercase tracking-widest text-[#d8b4fe]">En curso</div>
            </div>
            <div className="border border-[#3b126d] bg-[#140b22] rounded-xl p-5" data-testid="stat-cerrado">
              <CheckCircle2 className="w-5 h-5 text-emerald-300 mb-2" />
              <div className="f-poster text-3xl text-emerald-300">{stats?.by_status?.cerrado ?? "—"}</div>
              <div className="f-mono text-[10px] uppercase tracking-widest text-[#d8b4fe]">Cerradas</div>
            </div>
          </div>

          {stats?.by_service && Object.keys(stats.by_service).length > 0 && (
            <div className="mt-4 border border-[#3b126d] bg-[#140b22] rounded-xl p-5" data-testid="stat-by-service">
              <div className="f-mono text-[10px] uppercase tracking-widest text-[#d8b4fe] mb-3">Demanda por servicio</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.by_service).map(([svc, count]) => (
                  <span key={svc} className="text-xs f-mono bg-[#2e1065]/60 border border-[#581c87] rounded px-3 py-1.5 text-[#f5f3ff]">
                    {svc}: <span className="text-[#e879f9] font-bold">{count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Leads */}
        <section data-testid="leads-section">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <h2 className="f-poster text-2xl uppercase text-white">Solicitudes de Contacto</h2>
            <div className="flex gap-2 flex-wrap" data-testid="status-filters">
              {["todos", ...Object.keys(STATUS_LABELS)].map((s) => (
                <button key={s} onClick={() => setFilter(s)}
                  className={`f-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded border transition-colors ${filter === s ? "bg-[#e879f9] text-[#0d0914] border-[#e879f9]" : "border-[#581c87] text-[#d8b4fe] hover:border-[#e879f9]"}`}
                  data-testid={`filter-${s}`}>
                  {s === "todos" ? "Todos" : STATUS_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="f-mono text-xs uppercase tracking-widest text-[#a855f7] animate-pulse" data-testid="leads-loading">Cargando solicitudes...</div>
          ) : filtered.length === 0 ? (
            <div className="border border-dashed border-[#581c87] rounded-2xl p-10 text-center bg-[#170e28]/50" data-testid="leads-empty">
              <p className="text-sm text-[#d8b4fe]">No hay solicitudes en este estado todavía.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="leads-list">
              {filtered.map((lead) => (
                <div key={lead.id} className="border border-[#3b126d] bg-[#140b22] rounded-xl p-5 hover:border-[#e879f9]/50 transition-colors" data-testid={`lead-card-${lead.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="f-poster text-lg uppercase text-white" data-testid={`lead-name-${lead.id}`}>{lead.name}</div>
                      <div className="f-mono text-[11px] text-[#38bdf8]">{lead.email}</div>
                      <div className="f-mono text-[10px] uppercase tracking-widest text-[#a855f7] mt-1">
                        {lead.service} · {new Date(lead.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    <span className={`f-mono text-[10px] uppercase tracking-widest px-3 py-1 rounded border ${STATUS_COLORS[lead.status] || STATUS_COLORS.nuevo}`} data-testid={`lead-status-${lead.id}`}>
                      {STATUS_LABELS[lead.status] || lead.status}
                    </span>
                  </div>
                  <p className="text-xs text-[#d8b4fe] leading-relaxed mt-3 border-l-2 border-[#581c87] pl-3" data-testid={`lead-message-${lead.id}`}>{lead.message}</p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <button onClick={() => { setChargeFor(chargeFor === lead.id ? null : lead.id); setLastCheckout(null); }}
                      className="f-mono text-[10px] uppercase tracking-widest border border-emerald-400/40 text-emerald-300 px-3 py-1.5 rounded hover:bg-emerald-400/10 transition-colors flex items-center gap-1"
                      data-testid={`lead-charge-toggle-${lead.id}`}>
                      <Euro className="w-3 h-3" /> Cobrar
                    </button>
                    <button onClick={() => { setReplyFor(replyFor === lead.id ? null : lead.id); setReplyText(""); }}
                      className="f-mono text-[10px] uppercase tracking-widest border border-[#38bdf8]/40 text-[#38bdf8] px-3 py-1.5 rounded hover:bg-[#38bdf8]/10 transition-colors flex items-center gap-1"
                      data-testid={`lead-reply-toggle-${lead.id}`}>
                      <Mail className="w-3 h-3" /> Responder
                    </button>
                    {Object.keys(STATUS_LABELS).filter((s) => s !== lead.status).map((s) => (
                      <button key={s} onClick={() => setStatus(lead.id, s)}
                        className="f-mono text-[10px] uppercase tracking-widest border border-[#581c87] text-[#d8b4fe] px-3 py-1.5 rounded hover:border-[#e879f9] hover:text-white transition-colors"
                        data-testid={`lead-set-${s}-${lead.id}`}>
                        → {STATUS_LABELS[s]}
                      </button>
                    ))}
                    <button onClick={() => removeLead(lead.id)}
                      className="f-mono text-[10px] uppercase tracking-widest border border-red-500/40 text-red-400 px-3 py-1.5 rounded hover:bg-red-500/10 transition-colors flex items-center gap-1"
                      data-testid={`lead-delete-${lead.id}`}>
                      <Trash2 className="w-3 h-3" /> Eliminar
                    </button>
                  </div>
                  {replyFor === lead.id && (
                    <div className="mt-4 border border-[#38bdf8]/30 rounded-xl p-4 bg-[#0d0914]" data-testid={`lead-reply-box-${lead.id}`}>
                      <label className="block text-[10px] f-mono uppercase tracking-widest text-[#38bdf8] mb-2">
                        Respuesta por email a {lead.email}
                      </label>
                      <textarea rows={3} value={replyText} onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Escribe tu respuesta directa y confidencial..."
                        className="w-full bg-[#140b22] border border-[#581c87] rounded px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-[#38bdf8] transition-colors"
                        data-testid={`lead-reply-input-${lead.id}`} />
                      <button onClick={() => sendReply(lead.id)} disabled={sendingReply || !replyText.trim()}
                        className="mt-3 bg-[#38bdf8] text-[#0d0914] f-poster text-xs uppercase tracking-wide px-5 py-2.5 rounded hover:bg-white transition-colors disabled:opacity-50"
                        data-testid={`lead-reply-send-${lead.id}`}>
                        {sendingReply ? "Enviando..." : "Enviar Email"}
                      </button>
                    </div>
                  )}
                  {chargeFor === lead.id && (
                    <div className="mt-4 border border-emerald-400/30 rounded-xl p-4 bg-[#0d0914]" data-testid={`lead-charge-box-${lead.id}`}>
                      <label className="block text-[10px] f-mono uppercase tracking-widest text-emerald-300 mb-2">
                        Cobro online (Stripe · Visa / PayPal) para {lead.email}
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <input type="number" min="1" step="0.01" placeholder="Importe EUR" value={chargeAmount}
                          onChange={(e) => setChargeAmount(e.target.value)}
                          className="bg-[#140b22] border border-[#581c87] rounded px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-emerald-400 transition-colors"
                          data-testid={`charge-amount-${lead.id}`} />
                        <input type="text" placeholder="Concepto (ej. Pentesting API — fase 1)" value={chargeConcept}
                          onChange={(e) => setChargeConcept(e.target.value)}
                          className="sm:col-span-2 bg-[#140b22] border border-[#581c87] rounded px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-emerald-400 transition-colors"
                          data-testid={`charge-concept-${lead.id}`} />
                      </div>
                      <button onClick={() => createCharge(lead.id)} disabled={charging}
                        className="mt-3 bg-emerald-400 text-[#0d0914] f-poster text-xs uppercase tracking-wide px-5 py-2.5 rounded hover:bg-white transition-colors disabled:opacity-50"
                        data-testid={`charge-create-${lead.id}`}>
                        {charging ? "Creando..." : "Crear enlace de pago + email"}
                      </button>
                    </div>
                  )}
                  {lastCheckout && lastCheckout.leadId === lead.id && (
                    <div className="mt-3 border border-emerald-400/40 bg-emerald-400/10 rounded-xl p-4" data-testid={`checkout-link-box-${lead.id}`}>
                      <div className="f-mono text-[10px] uppercase tracking-widest text-emerald-300 mb-2">
                        Enlace de pago {lastCheckout.emailed ? "enviado por email" : "creado (cópialo y envíalo manualmente)"}:
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-[10px] f-mono text-[#d8b4fe] bg-[#0d0914] border border-[#581c87] rounded px-3 py-2 truncate" data-testid={`checkout-url-${lead.id}`}>{lastCheckout.url}</code>
                        <button onClick={() => copyCheckout(lastCheckout.url)} className="f-mono text-[10px] uppercase border border-[#581c87] text-white px-3 py-2 rounded hover:border-[#e879f9] transition-colors" data-testid={`copy-checkout-${lead.id}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                  {lead.replies?.length > 0 && (
                    <div className="mt-4 space-y-2 border-l-2 border-[#38bdf8]/40 pl-4" data-testid={`lead-replies-${lead.id}`}>
                      <div className="f-mono text-[10px] uppercase tracking-widest text-[#38bdf8]">Historial de respuestas ({lead.replies.length})</div>
                      {lead.replies.map((r, i) => (
                        <div key={i} className="bg-[#0d0914] border border-[#3b126d] rounded-lg p-3" data-testid={`reply-item-${lead.id}-${i}`}>
                          <div className="f-mono text-[10px] text-[#38bdf8]/80 mb-1">
                            ✉ {new Date(r.sent_at).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} · {r.by}
                          </div>
                          <p className="text-xs text-[#d8b4fe] leading-relaxed whitespace-pre-wrap">{r.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Payments */}
        <section data-testid="payments-section">
          <h2 className="f-poster text-2xl uppercase text-white mb-6 flex items-center gap-3">
            <CreditCard className="w-6 h-6 text-emerald-300" /> Cobros Online
          </h2>
          {payments.length === 0 ? (
            <div className="border border-dashed border-[#581c87] rounded-2xl p-10 text-center bg-[#170e28]/50" data-testid="payments-empty">
              <p className="text-sm text-[#d8b4fe]">Todavía no has creado ningún cobro. Usa el botón "Cobrar" en una solicitud.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="payments-list">
              {payments.map((p) => (
                <div key={p.session_id} className="border border-[#3b126d] bg-[#140b22] rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`payment-row-${p.session_id}`}>
                  <div>
                    <div className="f-poster text-base uppercase text-white">{p.concept}</div>
                    <div className="f-mono text-[10px] text-[#a855f7]">{p.lead_name} · {p.lead_email}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="f-poster text-xl text-white">{p.amount?.toFixed(2)} €</span>
                    <span className={`f-mono text-[10px] uppercase tracking-widest px-3 py-1 rounded border ${
                      p.payment_status === "paid" ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/40" :
                      p.payment_status === "pending" ? "bg-amber-400/15 text-amber-300 border-amber-400/40" :
                      "bg-red-400/15 text-red-300 border-red-400/40"
                    }`} data-testid={`payment-status-${p.session_id}`}>
                      {p.status === "partially_refunded" ? `Parc. devuelto (${p.refunded_amount?.toFixed(2)}€)` :
                       p.payment_status === "paid" ? "Pagado" :
                       p.payment_status === "pending" ? "Pendiente" :
                       p.payment_status === "refunded" ? "Reembolsado" : p.payment_status}
                    </span>
                    {p.invoice_number && (
                      <button onClick={() => downloadInvoice(p.session_id, p.invoice_number)} title={`Descargar factura ${p.invoice_number}`}
                        className="f-mono text-[10px] uppercase border border-[#581c87] text-white px-3 py-1.5 rounded hover:border-[#e879f9] transition-colors flex items-center gap-1"
                        data-testid={`invoice-download-${p.session_id}`}>
                        <FileText className="w-3.5 h-3.5" /> {p.invoice_number}
                      </button>
                    )}
                    {p.payment_status === "paid" && (
                      <button onClick={() => refundPayment(p)} title="Reembolsar (total o parcial)"
                        className="f-mono text-[10px] uppercase border border-red-500/40 text-red-300 px-3 py-1.5 rounded hover:bg-red-500/10 transition-colors flex items-center gap-1"
                        data-testid={`refund-${p.session_id}`}>
                        <Undo2 className="w-3.5 h-3.5" /> Reembolsar
                      </button>
                    )}
                    {p.payment_status === "pending" && (
                      <button onClick={() => copyCheckout(p.checkout_url)} className="f-mono text-[10px] uppercase border border-[#581c87] text-white px-3 py-1.5 rounded hover:border-[#e879f9] transition-colors" data-testid={`payment-copy-${p.session_id}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* CEO Encryption Vault */}
        <section data-testid="encryption-vault-section">
          <h2 className="f-poster text-2xl uppercase text-white mb-6 flex items-center gap-3">
            <KeyRound className="w-6 h-6 text-[#e879f9]" /> Clave de Cifrado CEO
          </h2>
          <div className="border border-[#3b126d] bg-[#140b22] rounded-2xl p-6" data-testid="encryption-key-card">
            <div className="flex items-center gap-2 f-mono text-[10px] uppercase tracking-widest text-[#e879f9] mb-4">
              <ShieldCheck className="w-4 h-4" /> RSA-4096 · Solo Super CEO Admin · La clave privada nunca sale del servidor cifrada
            </div>
            {encKey ? (
              <>
                <div className="f-mono text-[11px] text-[#38bdf8] mb-2" data-testid="key-fingerprint">
                  Fingerprint: {encKey.fingerprint}
                </div>
                <div className="f-mono text-[10px] text-[#a855f7] mb-4">
                  Generada: {new Date(encKey.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
                <pre className="bg-[#0d0914] border border-[#581c87] rounded-lg p-4 text-[10px] f-mono text-[#d8b4fe] overflow-x-auto max-h-48 whitespace-pre-wrap break-all" data-testid="public-key-block">
                  {encKey.public_key}
                </pre>
                <div className="flex flex-wrap gap-2 mt-4">
                  <button onClick={copyKey} className="f-mono text-[10px] uppercase tracking-widest border border-[#581c87] text-white px-4 py-2 rounded hover:border-[#e879f9] transition-colors flex items-center gap-2" data-testid="copy-key-btn">
                    <Copy className="w-3.5 h-3.5" /> Copiar clave pública
                  </button>
                  <button onClick={downloadKey} className="f-mono text-[10px] uppercase tracking-widest border border-[#581c87] text-white px-4 py-2 rounded hover:border-[#e879f9] transition-colors flex items-center gap-2" data-testid="download-key-btn">
                    <Download className="w-3.5 h-3.5" /> Descargar .asc
                  </button>
                  <button onClick={rotateKey} disabled={rotating} className="f-poster uppercase text-xs tracking-wide bg-[#e879f9] text-[#0d0914] px-4 py-2 rounded hover:bg-white transition-colors disabled:opacity-50" data-testid="rotate-key-btn">
                    {rotating ? "Regenerando..." : "Rotar clave"}
                  </button>
                </div>
              </>
            ) : (
              <div className="f-mono text-xs uppercase tracking-widest text-[#a855f7] animate-pulse" data-testid="key-loading">Generando clave...</div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
